import React, { useEffect, useState } from "react";
import clsx from "clsx";
import "./PropertyInfo.scss";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Modal } from "@mui/material";
import toast from "react-hot-toast";
import { TemplatesApi } from "../../../../../utils/api/Templates";
import { PropertyApi } from "../../../../../utils/api/Property";
import { CommonEnums } from "../../../../../enums/common.enums";
import FaceBookTrueIcon from "../../../../../components/icons/FaceBookTrueIcon";
import InstagramTrueIcon from "../../../../../components/icons/InstagramTrueIcon";
import LinkedInIcon from "../../../../../components/icons/LinkedInIcon";
import YouTubeIcon from "../../../../../components/icons/YouTubeIcon";
import TikTokIcon from "../../../../../components/icons/TikTokIcon";
import PreviewCircleIcon from "../../../../../components/icons/PreviewCircleIcon";
import DownloadIcon from "../../../../../components/icons/DownloadIcon";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import Loading from "../../../../loading/Loading";
import LeftArrowIcon from "../../../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../../../components/icons/RightArrowIcon";
import FacebookIcon from "../../../../../components/icons/FacebookIcon";
import InstagramIcon from "../../../../../components/icons/InstagramIcon";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import { clientRoutes } from "../../../../../utils/routes";
import { getDesignOutputImageUrl } from "../../../../../utils/helpers/common";
import PreviewModal from "../PreviewModal";

interface PropertyStageProps {
  onStageSelect: (stageLabel: string, templateType: string) => void;
  onContinue: () => void;
}

let propertyStages = {
  property_marketing: {
    rent: [
      {
        label: "Coming Soon - For Rent",
        stage: "coming_soon_property_marketing_for_rent",
      },
      {
        label: "For Rent Listing",
        stage: "for_rent_listing_property_marketing_for_rent",
      },
      {
        label: "Open Home",
        stage: "open_home_property_marketing_for_rent",
      },
      {
        label: "Deposit taken",
        stage: "deposit_taken_property_marketing_for_rent",
      },
    ],

    sale: [
      {
        label: "Coming Soon - For Sale",
        stage: "coming_soon_property_marketing_for_sale",
      },
      {
        label: "For Sale / Just Listed",
        stage: "for_sale_listing_property_marketing_for_sale",
      },
      {
        label: "Auction",
        stage: "auction_property_marketing_for_sale",
      },
      {
        label: "Open Home",
        stage: "open_home_property_marketing_for_sale",
      },
      {
        label: "Under Offer / Contract",
        stage: "under_offer_property_marketing_for_sale",
      },
    ],
  },

  agent_marketing: {
    rent: [
      {
        label: "Leased",
        stage: "leased_agent_marketing_for_rent",
      },
      {
        label: "Review Post",
        stage: "review_post_agent_marketing_for_rent",
      },
      {
        label: "Review Recap",
        stage: "review_recap_agent_marketing_for_rent",
      },
      {
        label: "Leased Recap",
        stage: "leased_recap_agent_marketing_for_rent",
      },
    ],

    sale: [
      {
        label: "Sold",
        stage: "sold_agent_marketing_for_sale",
      },
      {
        label: "Review Post",
        stage: "review_post_agent_marketing_for_sale",
      },
      {
        label: "Review Recap",
        stage: "review_recap_agent_marketing_for_sale",
      },
      {
        label: "Under Offer/Contract Recap",
        stage: "under_offer_recap_agent_marketing_for_sale",
      },
      {
        label: "Sold Recap",
        stage: "sold_recap_agent_marketing_for_sale",
      },
    ],
  },
};

/**
 * Design stages - property marketing for sale
 */
let designStagesForSalePropertyMarketing = [
  {
    label: "Coming Soon - For Sale",
    stage_slug: "coming_soon_property_marketing_for_sale",

    /**
     * Possible post statuses are:
     * pending | draft | schedule_for_upload | issues_detected | completed
     */
    post_status: "pending",

    /**
     * Save the latest post for this stage here if any
     */
    latest_post: null,

    is_stage_skipped: false,
  },
  {
    label: "For Sale / Just Listed",
    stage_slug: "for_sale_listing_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Auction",
    stage_slug: "auction_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Open Home",
    stage_slug: "open_home_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Under Offer / Contract",
    stage_slug: "under_offer_property_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
];

/**
 * Design stages - property marketing for rent
 */
let designStagesForRentPropertyMarketing = [
  {
    label: "Coming Soon - For Rent",
    stage_slug: "coming_soon_property_marketing_for_rent",

    /**
     * Possible post statuses are:
     * pending | draft | schedule_for_upload | issues_detected | completed
     */
    post_status: "pending",

    /**
     * Save the latest post for this stage here if any
     */
    latest_post: null,

    is_stage_skipped: false,
  },
  {
    label: "For Rent Listing",
    stage_slug: "for_rent_listing_property_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Open Home",
    stage_slug: "open_home_property_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Deposit taken",
    stage_slug: "deposit_taken_property_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
];

/**
 * Design stages - agent marketing for sale
 */
let designStagesForSaleAgentMarketing = [
  {
    label: "Sold",
    stage_slug: "sold_agent_marketing_for_sale",

    /**
     * Possible post statuses are:
     * pending | draft | schedule_for_upload | issues_detected | completed
     */
    post_status: "pending",

    /**
     * Save the latest post for this stage here if any
     */
    latest_post: null,

    is_stage_skipped: false,
  },
  {
    label: "Review Post",
    stage_slug: "review_post_agent_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Review Recap",
    stage_slug: "review_recap_agent_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Under Offer/Contract Recap",
    stage_slug: "under_offer_recap_agent_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Sold Recap",
    stage_slug: "sold_recap_agent_marketing_for_sale",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
];

/**
 * Design stages - agent marketing for rent
 */
let designStagesForRentAgentMarketing = [
  {
    label: "Leased",
    stage_slug: "leased_agent_marketing_for_rent",

    /**
     * Possible post statuses are:
     * pending | draft | schedule_for_upload | issues_detected | completed
     */
    post_status: "pending",

    /**
     * Save the latest post for this stage here if any
     */
    latest_post: null,

    is_stage_skipped: false,
  },
  {
    label: "Review Post",
    stage_slug: "review_post_agent_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Review Recap",
    stage_slug: "review_recap_agent_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
  {
    label: "Leased Recap",
    stage_slug: "leased_recap_agent_marketing_for_rent",
    post_status: "pending",
    latest_post: null,
    is_stage_skipped: false,
  },
];

interface TemplateItem {
  slug: string;
  label: string;
  template_type: string;
  isStatic?: boolean;
  status?: string;
  config?: any;
  allow?: boolean;
}

interface HardCodedStage {
  label: string;
  status: string;
  type: string;
}

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const PropertyJourneyInfo: React.FC = () => {
  const navigate = useNavigate();
  const { propertyPostId } = useParams<{ propertyPostId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionType =
    searchParams.get("subscription_type") || "property_marketing";
  const [listingType, setListingType] = useState<string>("sale");
  const postType = searchParams.get("post_type") || "image_post";
  const [propertyAddress, setPropertyAddress] = useState<string>(
    searchParams.get("property_address") || "",
  );
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [inputs, setInputs] = useState<{ [key: string]: any }>({});
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [stageVisible, setStageVisible] = useState(true);
  const inputOrder = ["sold_price", "punch_line"];

  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const [skippedStages, setSkippedStages] = useState<TemplateItem[]>([]);

  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);
  const [selectedActivityData, setSelectedActivityData] = useState<any>(null);
  const [journeyData, setJourneyData] = useState<any[]>([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const onStageSelect = (label: string, slug: string) => {
    console.log("Stage Selected:", label, slug);
  };

  useEffect(() => {
    const fetchPropertyPostDetails = async () => {
      if (!propertyPostId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await PropertyApi.getPropertyPostById(propertyPostId);
        const postData = res?.data?.data;

        if (postData) {
          const pId =
            typeof postData.property_id === "object"
              ? postData.property_id._id
              : postData.property_id;
          setPropertyId(pId);
          setPropertyAddress(postData.property_id?.property_address || "");

          const apiListingType =
            postData.property_id?.listing_type?.listing_type;
          if (apiListingType) {
            setListingType(apiListingType);
          }
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching details:", err);
        setLoading(false);
      }
    };
    fetchPropertyPostDetails();
  }, [propertyPostId]);

  const fetchJourney = async () => {
    if (!propertyId || !listingType) return;
    setLoading(true);
    const res = await PropertyApi.getJourneyView(propertyId, listingType);
    if (res.data?.success) {
      setJourneyData(res.data.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (propertyId && listingType) {
      fetchJourney();
    }
  }, [propertyId, listingType]);

  const hardCodedStages = [
    {
      label: "Auction",
      status: "pending",
      text: "Design generation is pending",
      type: "auction",
    },
    {
      label: "Sold",
      status: "pending",
      text: "Design generation is pending",
      type: "sold",
    },
    {
      label: "For Sale",
      status: "completed",
      text: "Completed",
      type: "for_sale",
    },
    {
      label: "Open Home",
      status: "scheduled",
      text: "Open home is scheduled and pending for upload",
      type: "open_home",
    },
    {
      label: "Coming Soon",
      status: "completed",
      text: "Completed",
      type: "coming_soon",
    },
  ];

  const extraStages: TemplateItem[] = hardCodedStages.map((stage) => ({
    slug: stage.type,
    label: stage.label,
    template_type: stage.type,
    isStatic: true,
    status: stage.status,
  }));

  useEffect(() => {
    const fetchPropertyPostDetails = async () => {
      if (!propertyPostId) return;
      try {
        const res = await PropertyApi.getPropertyPostById(propertyPostId);
        const postData = res?.data?.data;

        if (postData?.property_id) {
          let pId = null;
          if (typeof postData.property_id === "string") {
            pId = postData.property_id;
          } else if (typeof postData.property_id === "object") {
            pId = postData.property_id._id || postData.property_id.$oid;
          }
          if (pId) {
            setPropertyId(pId);
          }

          if (
            typeof postData.property_id === "object" &&
            postData.property_id.property_address
          ) {
            setPropertyAddress(postData.property_id.property_address);
          }
        }
      } catch (err) {
        console.error("Error fetching property post details:", err);
      }
    };

    fetchPropertyPostDetails();
  }, [propertyPostId]);

  const allCombinedStages = [...extraStages, ...templates];

  const mockExpandedItems = [
    {
      id: 1,
      image:
        "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794870/Frame_1261156603_urera9.png",
      orientation: "landscape",
      agent_socials: ["facebook", "instagram"],
      agency_socials: ["facebook", "linkedin"],
      scheduled_on: "Nov 30, 2024 10:00 AM",
      current_status: "Completed",
      posted_on: "Nov 30, 2024 02:00 PM",
      generated_on: "Nov 20, 2024 10:00 AM",
      last_updated: "Nov 30, 2024 10:00 AM",
    },
    {
      id: 2,
      image:
        "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
      orientation: "portrait",
      agent_socials: ["facebook", "instagram"],
      agency_socials: ["facebook", "linkedin"],
      scheduled_on: "Nov 30, 2024 10:00 AM",
      current_status: "Completed",
      posted_on: "Nov 30, 2024 02:00 PM",
      generated_on: "Nov 20, 2024 10:00 AM",
      last_updated: "Nov 30, 2024 10:00 AM",
    },
  ];

  const allowedSlugs = [
    "sold_listing_image_post_agent_marketing",
    "sold_listing_story_post_agent_marketing",
    "leased_image_post_agent_marketing",
    "leased_story_post_agent_marketing",
  ];

  const fetchTemplate = async (slug: string) => {
    setIsLoading(true);
    try {
      const response = await TemplatesApi.getTemplateConfigs({
        subscription_type: subscriptionType,
        listing_type: listingType,
        property_id: propertyId,
      });

      if (!response?.success || !response.data?.data) return;

      const postTypeBlock = response.data.data[postType];
      if (!postTypeBlock?.templates) return;

      const foundTemplate = postTypeBlock.templates.find(
        (tpl: any) => tpl.slug === slug && tpl.config?.design_template?._id,
      );

      if (!foundTemplate) return;

      const designTemplateId = foundTemplate.config.design_template._id;
      const res = await TemplatesApi.getDesignTemplateById(designTemplateId);

      if (res.success && res.data) {
        const apiInputs = res.data.data?.inputs || {};
        const defaultValues: { [key: string]: any } = {};
        let index = 0;

        Object.keys(apiInputs).forEach((key) => {
          const charLimit = apiInputs[key]?.characters_limit || 100;
          defaultValues[key] = {
            value: apiInputs[key]?.value || "",
            handler: apiInputs[key]?.handler || key,
            label: apiInputs[key]?.label || key,
            template_input_index: index,
            characters_limit: charLimit,
          };
          index += 1;
        });

        defaultValues["property_stage_title"] = {
          value: res.data.data?.template_type || "Property Stage",
          label: "Property Stage Title",
          template_input_index: 0,
          characters_limit: 200,
        };

        setInputs(apiInputs);
        setFormValues(defaultValues);

        setTemplateData({
          ...res.data,
          generatedImage: res.data?.data?.template_image_ref?.original_file_path
            ? `${process.env.REACT_APP_API_ENDPOINT}/${res.data.data.template_image_ref.original_file_path}`
            : res.data?.data?.template_image
              ? `${process.env.REACT_APP_API_ENDPOINT}/${res.data.data.template_image}`
              : null,
        });
      }
    } catch (err) {
      console.error("Failed to fetch property template:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStageTitleFromTemplateType = (templateType: string) => {
    let clean = templateType.replace(/(_agent_marketing|_listing|_post)/gi, "");
    const words = clean
      .split("_")
      .map((w) => w.toUpperCase().charAt(0) + w.slice(1));

    return words.join(" ");
  };

  useEffect(() => {
    const templateType = searchParams.get("template_type");
    const slug = searchParams.get("selected_slug");

    if (templateType && slug) {
      setStageVisible(false);
      setSelectedSlug(slug);

      if (allowedSlugs.includes(slug)) {
        // fetchTemplate(slug);
      } else {
        // onStageSelect("", templateType);
      }
    }
  }, [searchParams]);

  const handleSelect = async (item: any) => {
    const currentSlug = item.stage_slug || item.design_stage || item.slug;
    setSelectedSlug(currentSlug);

    try {
      setIsProcessing(true);
      setModalOpen(true);

      const res = await PropertyApi.createPostByStage({
        property_id: propertyId as string,
        design_stage: currentSlug,
        listing_type: listingType,
        template_type: currentSlug,
        subscription_type: subscriptionType,
      });

      if (res?.data?.success) {
        const postData = res.data.data;
        const newPostId = postData._id;
        const modelType = postData.model_type;

        if (modelType === "property_auctions") {
          navigate(
            `${clientRoutes.WorkspaceSchedule}?subscription_type=${subscriptionType}&auction_id=${newPostId}`,
          );
          return;
        }

        if (modelType === "open_home_schedules") {
          navigate(
            `${clientRoutes.scheduleOpenHome}?subscription_type=${subscriptionType}&open_home_schedule_id=${newPostId}`,
          );
          return;
        }

        const configStepKey = CommonEnums.propertyPostJourney.post_system.key;

        navigate(
          `/workspace/property-post-list/journey-view/${newPostId}?subscription_type=${subscriptionType}&step=${configStepKey}`,
        );
      } else {
        toast.error(res?.error || "Failed to create post record");
      }
    } catch (err) {
      console.error("Routing error:", err);
      toast.error("Something went wrong while routing the stage.");
    } finally {
      setIsProcessing(false);
      setModalOpen(false);
    }
  };

  const handleEditDraft = (latestPost: any, item: any) => {
    if (!latestPost?._id) return;

    const modelType = latestPost.model_type;
    const postId = latestPost._id;
    const currentSlug = item.stage_slug || item.design_stage || item.slug;

    if (modelType === "property_auctions") {
      navigate(
        `${clientRoutes.WorkspaceSchedule}?subscription_type=${subscriptionType}&auction_id=${postId}`,
      );
    } else if (modelType === "open_home_schedules") {
      navigate(
        `${clientRoutes.scheduleOpenHome}?subscription_type=${subscriptionType}&open_home_schedule_id=${postId}`,
      );
    } else {
      const configStepKey =
        CommonEnums.propertyPostJourney?.post_system?.key || "post_system";

      navigate(
        `/workspace/property-post-list/journey-view/${postId}?subscription_type=${subscriptionType}&step=${configStepKey}&template_type=${currentSlug}&selected_slug=${currentSlug}`,
      );

      if (postId === propertyPostId) {
        setStageVisible(false);
        setSelectedSlug(currentSlug);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("step", configStepKey);
          next.set("template_type", currentSlug);
          next.set("selected_slug", currentSlug);
          return next;
        });
        onStageSelect(item.label, currentSlug);
      }
    }
  };

  const handleUpdatedesign = async () => {
    if (!propertyPostId) {
      toast.error("Property Post ID is missing!");
      return;
    }

    const inputsPayload: any = {};
    Object.keys(formValues).forEach((key) => {
      const val = formValues[key];
      if (val?.value && (val.enabled ?? true)) {
        inputsPayload[key] = {
          handler: val.handler || key,
          label: val.label || key,
          value: val.value,
          template_input_index: val.template_input_index ?? 0,
          characters_limit: val.characters_limit ?? 0,
        };
      }
    });

    if (!selectedSlug) {
      toast.error("No template slug selected!");
      return;
    }

    const payload = {
      template_type: selectedSlug,
      finalize: true,
    };

    setModalOpen(true);

    try {
      const res = await PropertyApi.updatePropertyPost(propertyPostId, payload);
      if (res?.error) {
        toast.error(res.error || "Internal server error!");
        setModalOpen(false);
        return;
      }
      setModalOpen(false);
      setIsGenerating(true);
      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.getPropertyPostById(propertyPostId);
          if (check?.error) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }
          const propertyData = check?.data?.data;
          if (!propertyData) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }
          const imagePath =
            propertyData?.design_activity_ref?.design_task_id
              ?.output_image_path;
          if (!imagePath) {
            setTimeout(pollDesignStatus, pollInterval);
            return;
          }
          const assetUrl = `${API_BASE_URL}/${imagePath}`;
          setTemplateData((prev: any) => ({
            ...prev,
            generatedImage: assetUrl,
          }));
          setIsGenerating(false);
          toast.success("Design generated successfully!");
        } catch (err) {
          console.error("Polling error:", err);
          setTimeout(pollDesignStatus, pollInterval);
        }
      };
      pollDesignStatus();
    } catch (err) {
      console.error("Update design error:", err);
      toast.error("Something went wrong while updating the design!");
      setModalOpen(false);
      setIsGenerating(false);
    }
  };

  const handleConfirm = async () => {
    if (!propertyPostId) {
      toast.error("Property Post ID is missing!");
      return;
    }
    if (!selectedSlug) {
      toast.error("No template slug selected!");
      return;
    }
    setIsProcessing(true);
    setModalOpen(true);
    try {
      const payload = {
        template_type: selectedSlug,
        finalize: false,
      };
      const res = await PropertyApi.updatePropertyPost(propertyPostId, payload);
      if (res?.error) {
        toast.error(res.error || "Failed to confirm the post stage.");
        return;
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", CommonEnums.propertyPost.post_configuration.key);
        return next;
      });
    } catch (err) {
      console.error("Confirm error:", err);
      toast.error("Something went wrong while confirming!");
    } finally {
      setIsProcessing(false);
      setModalOpen(false);
    }
  };

  const STATIC_STAGE_TYPES = [
    "auction",
    "sold",
    "for_sale",
    "open_home",
    "coming_soon",
  ];

  const handleViewPostConfig = async (template: TemplateItem) => {
    try {
      setIsProcessing(true);

      if (
        propertyPostId &&
        !STATIC_STAGE_TYPES.includes(template.template_type)
      ) {
        await PropertyApi.updatePropertyPost(propertyPostId, {
          template_type: template.template_type,
        });
      }

      setSelectedSlug(template.slug);
      setStageVisible(false);

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        next.set("stage_label", template.label);
        next.set("step", CommonEnums.propertyPost.post_configuration.key);

        if (!STATIC_STAGE_TYPES.includes(template.template_type)) {
          next.set("template_type", template.template_type);
          next.set("selected_slug", template.slug);
        } else {
          next.delete("template_type");
          next.delete("selected_slug");
        }

        return next;
      });
    } catch (err) {
      console.error(err);
      toast.error("Unable to open post configuration.");
    } finally {
      setIsProcessing(false);
    }
  };

  const getPreviewContainerClass = (postType: string | null) => {
    switch (postType) {
      case CommonEnums.postType.story_post.key:
        return "story-post-preview";
      case CommonEnums.postType.video_post.key:
        return "video-post-preview";
      case CommonEnums.postType.reel_post.key:
        return "reel-post-preview";
      default:
        return "image-post-preview";
    }
  };

  const handleDownload = async (url: string, filename: string) => {
    if (!url || url.includes("fallback")) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "design.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Failed to download image");
    }
  };

  const handlePreviewOpen = (postItem: any) => {
    const imageUrl = getDesignOutputImageUrl(postItem.ref);
    if (!imageUrl || imageUrl.includes("fallback")) return;

    setPreviewImages([imageUrl]);
    setPreviewIndex(0);
    setPreviewModalOpen(true);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const toggleSkip = (item: any, isCurrentlySkipped: boolean) => {
    if (!propertyId) return;

    const stageSlug = item.stage_slug || item.design_stage;
    const nextSkippedValue = !isCurrentlySkipped;

    setJourneyData((prevJourney) =>
      prevJourney.map((stage) => {
        const currentStageSlug = stage.stage_slug || stage.design_stage;
        if (currentStageSlug === stageSlug) {
          return { ...stage, is_stage_skipped: nextSkippedValue };
        }
        return stage;
      }),
    );

    setTimeout(async () => {
      try {
        const res = await PropertyApi.updatePropertyJourney({
          property_id: propertyId,
          design_stage: stageSlug,
          is_skipped: nextSkippedValue,
        });

        if (res.data?.success) {
          const silentRes = await PropertyApi.getJourneyView(
            propertyId,
            listingType,
          );
          if (silentRes.data?.success) {
            setJourneyData(silentRes.data.data);
          }
        }
      } catch (err) {
        console.error("Skip API update failed:", err);
        toast.error("Failed to update stage status");
      }
    }, 500);
  };

  useEffect(() => {
    const step = searchParams.get("step");
    const templateType = searchParams.get("template_type");
    const slug = searchParams.get("selected_slug");

    if (step === "post_configuration" && templateType && slug) {
      setStageVisible(false);
      setSelectedSlug(slug);
    } else {
      setStageVisible(true);
    }
  }, [searchParams]);

  const handleToggleAccordion = (slug: string) => {
    if (expandedStage === slug) {
      setExpandedStage(null);
    } else {
      setExpandedStage(slug);
    }
  };

  const renderSocialIcons = (data: any) => {
    const isActive = (platform: string) => {
      if (Array.isArray(data)) return data.includes(platform);
      if (data && typeof data === "object")
        return data[platform]?.status === "uploaded";
      return false;
    };
    return (
      <div className="activity-social-icons">
        <div className={clsx("social-icon", isActive("facebook") && "active")}>
          <FacebookIcon />
        </div>
        <div className={clsx("social-icon", isActive("instagram") && "active")}>
          <InstagramIcon />
        </div>
        <div className={clsx("social-icon", isActive("linkedin") && "active")}>
          <LinkedInIcon />
        </div>
        <div className={clsx("social-icon", isActive("youtube") && "active")}>
          <YouTubeIcon />
        </div>
        <div className={clsx("social-icon", isActive("tiktok") && "active")}>
          <TikTokIcon />
        </div>
      </div>
    );
  };

  const ActivityImage: React.FC<{
    src: string;
    alt: string;
    orientation?: string;
  }> = ({ src, alt, orientation }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    return (
      <div
        className={clsx(
          "activity-image-section",
          orientation === "portrait" && "story-post-image-container",
        )}
      >
        {loading && !error && (
          <div className="image-skeleton-loader">
            <div className="skeleton-shimmer" />
          </div>
        )}
        <img
          src={src}
          alt={alt}
          className="activity-thumbnail"
          style={{ opacity: loading ? 0 : 1, transition: "opacity 0.3s ease" }}
          onLoad={() => setLoading(false)}
          onError={(e) => {
            setLoading(false);
            setError(true);
            e.currentTarget.onerror = null;
            e.currentTarget.src =
              "/images/square-fallback-image-for-image-preview.png";
          }}
        />
      </div>
    );
  };

  const renderActivityItem = (
    postItem: any,
    latestPost: any,
    index: number,
  ) => (
    <div key={index} className="activity-card-wrapper">
      <div className="activity-card">
        {/* <div
          className={clsx(
            "activity-image-section",
            postItem.orientation === "portrait" && "story-post-image-container",
          )}
        > */}
        {/* <img
            src={
              postItem.ref?.design_task_id?.output_image_path
                ? CommonHelpers.getImageUrlFromImageTool({
                    path: postItem.ref.design_task_id.output_image_path,
                  })
                : postItem.fallbackImage
            }
            alt={postItem.title}
            className="activity-thumbnail"
            onError={(e) => {
              e.currentTarget.src = postItem.fallbackImage;
            }}
          /> */}
        {/* <img
            src={getDesignOutputImageUrl(postItem.ref)}
            alt={postItem.title}
            className="activity-thumbnail"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/square-fallback-image-for-image-preview.png";
            }}
          />
        </div> */}
        <ActivityImage
          src={getDesignOutputImageUrl(postItem.ref)}
          alt={postItem.title}
          orientation={postItem.orientation}
        />

        <div className="activity-content-section">
          <div className="activity-social-icons-wrapper">
            <div className="social-block">
              <p className="social-title">Agent Social</p>
              <div className="activity-social-icons">
                {renderSocialIcons(latestPost?.agent_socials || latestPost)}
              </div>
            </div>
            <div className="social-block">
              <p className="social-title">Agency Social</p>
              <div className="activity-social-icons">
                {renderSocialIcons(latestPost?.agency_socials || latestPost)}
              </div>
            </div>
          </div>

          <div className="activity-details">
            <div className="detail-row">
              <div>
                <div className="label">Scheduled On</div>
                <div className="value">
                  {latestPost?.schedule_datetime
                    ? new Date(latestPost.schedule_datetime).toLocaleString(
                        "en-US",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )
                    : "-"}
                </div>
              </div>
              <div>
                <div className="label">Current Status</div>
                <div className="value" style={{ textTransform: "capitalize" }}>
                  {latestPost?.post_status
                    ? latestPost.post_status.replace(/_/g, " ")
                    : "-"}
                </div>
              </div>
              <div>
                <div className="label">Posted On</div>
                <div className="value">
                  {latestPost?.post_status === "completed" &&
                  latestPost.updatedAt
                    ? new Date(latestPost.updatedAt).toLocaleString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"}
                </div>
              </div>
              <div>
                <div className="label">Generated On</div>
                <div className="value">
                  {latestPost?.createdAt
                    ? new Date(latestPost.createdAt).toLocaleString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"}
                </div>
              </div>
              <div>
                <div className="label">Last Updated On</div>
                <div className="value">
                  {latestPost?.updatedAt
                    ? new Date(latestPost.updatedAt).toLocaleString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "-"}
                </div>
              </div>
            </div>
          </div>

          {/* <div className="activity-bottom-actions">
            <button
              className="btn-preview"
              disabled={!postItem.ref}
              onClick={() => {
                const path = postItem.ref?.design_task_id?.output_image_path;
                if (path)
                  window.open(
                    CommonHelpers.getImageUrlFromImageTool({ path }),
                    "_blank",
                  );
              }}
            >
              <PreviewCircleIcon /> Preview
            </button>
            <button
              className="btn-download"
              disabled={!postItem.ref}
              onClick={() => {
                const url = CommonHelpers.getImageUrlFromImageTool({
                  path: postItem.ref?.design_task_id?.output_image_path,
                });
                if (url) window.open(url, "_blank");
              }}
            >
              <DownloadIcon /> Download
            </button>
          </div> */}
          <div className="activity-bottom-actions">
            <button
              className="btn-preview"
              disabled={!postItem.ref}
              onClick={() => handlePreviewOpen(postItem)}
            >
              <PreviewCircleIcon /> Preview
            </button>
            <button
              className="btn-download"
              disabled={!postItem.ref}
              onClick={() => {
                const url = getDesignOutputImageUrl(postItem.ref);
                const filename = `${postItem.title || "design"}-${Date.now()}.png`;
                handleDownload(url, filename);
              }}
            >
              <DownloadIcon /> Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStageCard = (item: any, index: number) => {
    const isSkipped = item.is_stage_skipped;
    const stageSlug = item.stage_slug || item.design_stage;
    const status = item.post_status;
    const label = item.label;

    const isCompleted =
      status === "completed" ||
      status === "scheduled_for_upload" ||
      status === "issues_detected";
    const isPending = status === "pending";
    const isDraft = status === "draft";
    const isExpanded = expandedStage === stageSlug;

    if (isSkipped || isPending) {
      return (
        <div
          key={stageSlug + index}
          className={`stage-card ${isSkipped ? "skipped" : ""}`}
        >
          <div className="stage-left">
            <div className="stage-icon">
              <img src="/images/image-placeholder.svg" alt="icon" />
            </div>
            <div className="stage-info">
              <h4>{label}</h4>
              <p>
                {isSkipped
                  ? "This stage was skipped"
                  : "Design generation is pending"}
              </p>
            </div>
          </div>
          <div className="stage-actions">
            <div className="skip-toggle">
              <span>Skip this stage</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isSkipped}
                  onChange={() => toggleSkip(item, isSkipped)}
                />
                <span className="slider round"></span>
              </label>
            </div>
            <button
              className="create-post-btn"
              disabled={isSkipped}
              onClick={() => handleSelect(item)}
            >
              Create post
            </button>
          </div>
        </div>
      );
    }

    const latestPost = item.latest_post;

    return (
      <div
        key={stageSlug + index}
        className={`stage-card completed-accordion ${isExpanded ? "expanded" : ""}`}
      >
        <div
          className="accordion-header"
          onClick={() => handleToggleAccordion(stageSlug)}
        >
          <div className="header-left">
            <div
              className={clsx(
                "status-icon-box",
                status === "completed" ? "success" : "neutral",
              )}
            >
              <img
                src={
                  status === "completed"
                    ? "/images/True-icon-with-square.svg"
                    : "/images/timeicon.svg"
                }
                alt=""
              />
            </div>
            <div className="header-info">
              <h4>{label}</h4>
              <p>
                {status === "completed"
                  ? "Completed"
                  : status === "draft"
                    ? "Draft"
                    : "Post in progress"}
              </p>
            </div>
          </div>

          <div className="header-right-actions">
            {isDraft && latestPost && (
              <button
                className="create-post-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleEditDraft(latestPost, item);
                }}
              >
                Edit draft
              </button>
            )}

            <div className={`header-chevron ${isExpanded ? "rotate" : ""}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 9L12 15L18 9"
                  stroke="#1A1A1A"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {isExpanded && latestPost && (
          <div className="accordion-body">
            {(() => {
              // const displayItems = [
              //   {
              //     ref: latestPost.image_post_design_activity_ref,
              //     title: "Image Post",
              //     orientation: "landscape",
              //     fallbackImage:
              //       "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794870/Frame_1261156603_urera9.png",
              //   },
              //   {
              //     ref: latestPost.story_post_design_activity_ref,
              //     title: "Story Post",
              //     orientation: "portrait",
              //     fallbackImage:
              //       "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
              //   },
              // ];

              const displayItems = [
                {
                  ref: latestPost.image_post_output_ref,
                  title: "Image Post",
                  orientation: "landscape",
                },
                {
                  ref: latestPost.story_post_output_ref,
                  title: "Story Post",
                  orientation: "portrait",
                },
              ];

              return displayItems.map((postItem, i) => (
                <div key={i} className="activity-card-wrapper">
                  <div className="activity-card">
                    {/* <div
                      className={clsx(
                        "activity-image-section",
                        postItem.orientation === "portrait" &&
                          "story-post-image-container",
                      )}
                    > */}
                    {/* <img
                        src={
                          postItem.ref?.design_task_id?.output_image_path
                            ? CommonHelpers.getImageUrlFromImageTool({
                                path: postItem.ref.design_task_id
                                  .output_image_path,
                              })
                            : postItem.fallbackImage
                        }
                        alt="Design Preview"
                        className="activity-thumbnail"
                        onError={(e) => {
                          e.currentTarget.src = postItem.fallbackImage;
                        }}
                      /> */}
                    {/* <img
                        src={getDesignOutputImageUrl(postItem.ref)}
                        alt="Design Preview"
                        className="activity-thumbnail"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src =
                            "/images/square-fallback-image-for-image-preview.png";
                        }}
                      />
                    </div> */}
                    <ActivityImage
                      src={getDesignOutputImageUrl(postItem.ref)}
                      alt="Design Preview"
                      orientation={postItem.orientation}
                    />
                    <div className="activity-content-section">
                      <div className="activity-social-icons-wrapper">
                        <div className="social-block">
                          <p className="social-title">Agent Social</p>
                          <div className="activity-social-icons">
                            {renderSocialIcons(
                              latestPost?.agent_socials || latestPost,
                            )}
                          </div>
                        </div>
                        <div className="social-block">
                          <p className="social-title">Agency Social</p>
                          <div className="activity-social-icons">
                            {renderSocialIcons(
                              latestPost?.agency_socials || latestPost,
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="activity-details">
                        <div className="detail-row">
                          <div>
                            <div className="label">Scheduled On</div>
                            <div className="value">
                              {latestPost.schedule_datetime
                                ? new Date(
                                    latestPost.schedule_datetime,
                                  ).toLocaleString("en-US", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                : "-"}
                            </div>
                          </div>
                          <div>
                            <div className="label">Current Status</div>
                            <div
                              className="value"
                              style={{ textTransform: "capitalize" }}
                            >
                              {latestPost.post_status?.replace(/_/g, " ") ||
                                "-"}
                            </div>
                          </div>
                          <div>
                            <div className="label">Posted On</div>
                            <div className="value">
                              {latestPost.post_status === "completed"
                                ? new Date(latestPost.updatedAt).toLocaleString(
                                    "en-US",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    },
                                  )
                                : "-"}
                            </div>
                          </div>
                          <div>
                            <div className="label">Generated On</div>
                            <div className="value">
                              {latestPost.createdAt
                                ? new Date(latestPost.createdAt).toLocaleString(
                                    "en-US",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    },
                                  )
                                : "-"}
                            </div>
                          </div>
                          <div>
                            <div className="label">Last Updated On</div>
                            <div className="value">
                              {latestPost.updatedAt
                                ? new Date(latestPost.updatedAt).toLocaleString(
                                    "en-US",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    },
                                  )
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* <div className="activity-bottom-actions">
                        <button
                          className="btn-preview"
                          disabled={!postItem.ref}
                          onClick={() => {
                            if (postItem.ref)
                              window.open(
                                CommonHelpers.getImageUrlFromImageTool({
                                  path: postItem.ref.design_task_id
                                    .output_image_path,
                                }),
                                "_blank",
                              );
                          }}
                        >
                          Preview
                        </button>
                        <button
                          className="btn-download"
                          disabled={!postItem.ref}
                        >
                          Download
                        </button>
                      </div> */}
                      <div className="activity-bottom-actions">
                        <button
                          className="btn-preview"
                          disabled={!postItem.ref}
                          onClick={() => handlePreviewOpen(postItem)}
                        >
                          Preview
                        </button>
                        <button
                          className="btn-download"
                          disabled={!postItem.ref}
                          onClick={() => {
                            const url = getDesignOutputImageUrl(postItem.ref);
                            const filename = `${postItem.title || "design"}-${Date.now()}.png`;
                            handleDownload(url, filename);
                          }}
                        >
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
            <div className="accordion-footer-actions">
              <button
                className="regenerate-btn"
                onClick={() => {
                  setSelectedActivityData({
                    label: label,
                    status:
                      status === "completed" ? "Completed" : "In Progress",
                    post: latestPost,
                  });
                  setViewAllModalOpen(true);
                }}
              >
                View activity
              </button>
              <button
                className="view-all-btn"
                onClick={() => handleSelect(item)}
              >
                View post config
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const activeList = journeyData.filter((stage) => !stage.is_stage_skipped);
  const skippedList = journeyData.filter((stage) => stage.is_stage_skipped);

  return (
    <div className="property-stage-container">
      <div className="manage-invites-list-heading">
        <div className="info-propertie-view-back-btn" onClick={handleBack}>
          <p className="bakc-text-contain">Property address</p>
        </div>
        <p className="page-title">{propertyAddress}</p>
      </div>

      {stageVisible && (
        <>
          <h2 className="stage-title">Property marketing journey</h2>

          {loading ? (
            <div className="loading-wrapper">
              <Loading />
            </div>
          ) : (
            <div className="property-marketing-journey">
              {activeList.map((template, idx) =>
                renderStageCard(template, idx),
              )}
            </div>
          )}

          <div className="skipped-stages">
            <h4>Skipped stages</h4>

            {skippedList.length === 0 ? (
              <div className="empty-skipped">
                <p>No Skipped Stages</p>
                <span>Your skipped history is currently empty</span>
              </div>
            ) : (
              skippedList.map((template, idx) => renderStageCard(template, idx))
            )}
          </div>
        </>
      )}

      <Modal
        open={viewAllModalOpen}
        onClose={() => setViewAllModalOpen(false)}
        className="view-all-modal"
      >
        <div className="property-info-view-all-modal-wrapper">
          <div className="property-info-modal-header">
            <h3>All Updates</h3>
            <button
              className="close-btn"
              onClick={() => setViewAllModalOpen(false)}
            >
              Close
            </button>
          </div>

          <div className="stage-status-bar">
            <div className="status-icon">
              <img
                src={
                  selectedActivityData?.status === "Completed"
                    ? "/images/True-icon-with-square.svg"
                    : "/images/timeicon.svg"
                }
                alt="Status"
              />
            </div>
            <div className="status-info">
              <h4>{selectedActivityData?.label || "Stage"}</h4>
              <p>{selectedActivityData?.status || "Status"}</p>
            </div>
          </div>

          <div className="property-info-modal-scroll-body">
            {selectedActivityData?.post ? (
              (() => {
                const displayItems = [
                  {
                    ref: selectedActivityData.post.image_post_output_ref,
                    type: "image",
                    orientation: "landscape",
                    title: "Image Post",
                  },
                  {
                    ref: selectedActivityData.post.story_post_output_ref,
                    type: "story",
                    orientation: "portrait",
                    title: "Story Post",
                  },
                ];
                // const displayItems = [
                //   {
                //     ref: selectedActivityData.post
                //       .image_post_design_activity_ref,
                //     type: "image",
                //     orientation: "landscape",
                //     title: "Image Post",
                //     fallbackImage:
                //       "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794870/Frame_1261156603_urera9.png",
                //   },
                //   {
                //     ref: selectedActivityData.post
                //       .story_post_design_activity_ref,
                //     type: "story",
                //     orientation: "portrait",
                //     title: "Story Post",
                //     fallbackImage:
                //       "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794813/image-post-1d4fd5a1-8947-11f0-af12-a989c10daa3f-1756959897594_2_fvbph6.png",
                //   },
                // ];
                return displayItems.map((item, idx) =>
                  renderActivityItem(item, selectedActivityData.post, idx),
                );
              })()
            ) : (
              <p style={{ textAlign: "center", marginTop: "20px" }}>
                No activity found.
              </p>
            )}
          </div>

          <div className="property-info-modal-footer">
            <span>
              Viewing {selectedActivityData?.post ? 1 : 0}-
              {selectedActivityData?.post ? 2 : 0} of{" "}
              {selectedActivityData?.post ? 2 : 0} results
            </span>
            <div className="pagination-arrows">
              <button disabled>
                <LeftArrowIcon />
              </button>
              <button disabled>
                <RightArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={modalOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setModalOpen(false);
          }
        }}
        aria-labelledby="processing-modal"
        className="add-property-modal styled-modal"
        disableEscapeKeyDown
      >
        <div className="modal-center-wrapper">
          <div className="loading-content-wrapper">
            <div className="loading-container">
              <div className="spinner" />
              <p className="loading-text">
                Processing your request
                <span className="desktop-break">
                  <br />
                </span>
                Please wait...
              </p>
            </div>
          </div>
        </div>
      </Modal>

      <PreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        images={previewImages}
        initialIndex={previewIndex}
      />
    </div>
  );
};

export default PropertyJourneyInfo;
